import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  MessageSquare,
  Plus,
  Edit,
  Trash2,
  Loader2,
  X,
  Star,
  Eye,
  EyeOff,
  Save,
  User as UserIcon,
} from 'lucide-react'
import { apiGet, apiPost, apiPut, apiDelete } from '../lib/api'

/**
 * Testimonios — administración de testimonios mostrados en el home público.
 * Sigue el patrón Envios.tsx (header con icon + título) y Paginas.tsx (tabla CRUD).
 *
 * Endpoints:
 *  - GET/POST /api/admin/testimonios
 *  - GET/PUT/DELETE /api/admin/testimonios/[id]
 */

interface Testimonio {
  id: number | string
  nombre: string
  cargo: string | null
  comentario: string
  rating: number
  avatar_url: string | null
  is_active: boolean
  sort_order: number
  created_at?: string | null
  updated_at?: string | null
}

interface TestimoniosResponse {
  items: Testimonio[]
}

interface FormState {
  nombre: string
  cargo: string
  comentario: string
  rating: number
  avatar_url: string
  is_active: boolean
  sort_order: number
}

const EMPTY_FORM: FormState = {
  nombre: '',
  cargo: '',
  comentario: '',
  rating: 5,
  avatar_url: '',
  is_active: true,
  sort_order: 0,
}

function initials(nombre: string): string {
  return nombre
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}

function truncate(s: string, n: number): string {
  if (!s) return ''
  return s.length > n ? s.slice(0, n).trimEnd() + '…' : s
}

function StarsDisplay({ value }: { value: number }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${value} de 5 estrellas`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`size-4 ${
            i <= value ? 'fill-amber-400 text-amber-400' : 'text-ink-200'
          }`}
        />
      ))}
    </div>
  )
}

function StarsInput({
  value,
  onChange,
}: {
  value: number
  onChange: (v: number) => void
}) {
  const [hover, setHover] = useState<number | null>(null)
  const display = hover ?? value
  return (
    <div
      className="flex items-center gap-1"
      onMouseLeave={() => setHover(null)}
      role="radiogroup"
      aria-label="Rating"
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <button
          key={i}
          type="button"
          onClick={() => onChange(i)}
          onMouseEnter={() => setHover(i)}
          className="rounded p-0.5 transition-transform hover:scale-110"
          aria-label={`${i} estrella${i > 1 ? 's' : ''}`}
          aria-checked={value === i}
          role="radio"
        >
          <Star
            className={`size-6 ${
              i <= display ? 'fill-amber-400 text-amber-400' : 'text-ink-300'
            }`}
          />
        </button>
      ))}
      <span className="ml-2 text-sm font-medium text-ink-600">{display}/5</span>
    </div>
  )
}

function Avatar({ url, nombre }: { url: string | null; nombre: string }) {
  if (url) {
    return (
      <img
        src={url}
        alt={nombre}
        className="size-10 rounded-full object-cover ring-1 ring-ink-200"
        onError={(e) => {
          ;(e.currentTarget as HTMLImageElement).style.display = 'none'
        }}
      />
    )
  }
  return (
    <div className="grid size-10 place-items-center rounded-full bg-brand-100 text-sm font-bold text-brand-700 ring-1 ring-brand-200">
      {initials(nombre) || <UserIcon className="size-4" />}
    </div>
  )
}

export function Testimonios() {
  const qc = useQueryClient()
  const { data, isLoading, isFetching } = useQuery<TestimoniosResponse>({
    queryKey: ['testimonios-admin'],
    queryFn: () => apiGet('/api/admin/testimonios'),
  })

  // Local sort_order edits for inline number inputs
  const [sortDraft, setSortDraft] = useState<Record<string, number>>({})

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | string | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (data?.items) {
      const init: Record<string, number> = {}
      for (const t of data.items) init[String(t.id)] = t.sort_order
      setSortDraft(init)
    }
  }, [data])

  const toggleActive = useMutation({
    mutationFn: (t: Testimonio) =>
      apiPut(`/api/admin/testimonios/${t.id}`, { is_active: !t.is_active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['testimonios-admin'] }),
  })

  const updateSort = useMutation({
    mutationFn: ({ id, value }: { id: number | string; value: number }) =>
      apiPut(`/api/admin/testimonios/${id}`, { sort_order: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['testimonios-admin'] }),
  })

  const del = useMutation({
    mutationFn: (id: number | string) => apiDelete(`/api/admin/testimonios/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['testimonios-admin'] }),
  })

  const create = useMutation({
    mutationFn: (payload: FormState) => apiPost('/api/admin/testimonios', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['testimonios-admin'] })
      closeModal()
    },
    onError: (err: Error) => setFormError(err.message || 'Error al crear'),
  })

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: FormState }) =>
      apiPut(`/api/admin/testimonios/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['testimonios-admin'] })
      closeModal()
    },
    onError: (err: Error) => setFormError(err.message || 'Error al actualizar'),
  })

  function openNew() {
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError(null)
    setModalOpen(true)
  }

  function openEdit(t: Testimonio) {
    setEditingId(t.id)
    setForm({
      nombre: t.nombre ?? '',
      cargo: t.cargo ?? '',
      comentario: t.comentario ?? '',
      rating: t.rating ?? 5,
      avatar_url: t.avatar_url ?? '',
      is_active: !!t.is_active,
      sort_order: t.sort_order ?? 0,
    })
    setFormError(null)
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
    setEditingId(null)
    setForm(EMPTY_FORM)
    setFormError(null)
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setFormError(null)

    const nombre = form.nombre.trim()
    const comentario = form.comentario.trim()
    if (!nombre) {
      setFormError('El nombre es obligatorio.')
      return
    }
    if (!comentario) {
      setFormError('El comentario es obligatorio.')
      return
    }
    if (form.rating < 1 || form.rating > 5) {
      setFormError('El rating debe estar entre 1 y 5.')
      return
    }

    const payload: FormState = {
      ...form,
      nombre,
      cargo: form.cargo.trim(),
      comentario,
      avatar_url: form.avatar_url.trim(),
      sort_order: Number(form.sort_order) || 0,
    }

    if (editingId == null) {
      create.mutate(payload)
    } else {
      update.mutate({ id: editingId, payload })
    }
  }

  function onDelete(t: Testimonio) {
    if (!confirm(`¿Eliminar testimonio de "${t.nombre}"? Esta acción no se puede deshacer.`)) return
    del.mutate(t.id)
  }

  function onCommitSort(t: Testimonio) {
    const v = Number(sortDraft[String(t.id)] ?? t.sort_order)
    if (!isFinite(v) || v === t.sort_order) return
    updateSort.mutate({ id: t.id, value: v })
  }

  const items = data?.items ?? []
  const saving = create.isPending || update.isPending

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="grid size-11 place-items-center rounded-lg bg-brand-50 text-brand-600">
            <MessageSquare className="size-5" strokeWidth={2.2} />
          </div>
          <div>
            <h1 className="text-2xl font-extrabold text-ink-900">Testimonios</h1>
            <p className="text-sm text-ink-500">
              Opiniones de clientes mostradas en el home público. Editables sin deploy.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openNew}
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700"
        >
          <Plus className="size-4" /> Nuevo testimonio
        </button>
      </div>

      <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}

        {!isLoading && items.length === 0 && (
          <div className="grid place-items-center gap-2 py-16 text-center text-sm text-ink-500">
            <MessageSquare className="size-8 text-ink-300" />
            <div>No hay testimonios todavía.</div>
            <button
              type="button"
              onClick={openNew}
              className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700"
            >
              <Plus className="size-3.5" /> Crear el primero
            </button>
          </div>
        )}

        {items.length > 0 && (
          <div className="relative overflow-x-auto">
            {isFetching && (
              <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                <Loader2 className="size-4 animate-spin text-brand-600" />
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="bg-ink-50">
                <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">Rating</th>
                  <th className="px-4 py-3 font-medium">Comentario</th>
                  <th className="px-4 py-3 font-medium">Estado</th>
                  <th className="px-4 py-3 font-medium">Orden</th>
                  <th className="w-24 px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => {
                  const sortVal = sortDraft[String(t.id)] ?? t.sort_order
                  return (
                    <tr
                      key={String(t.id)}
                      className="border-t border-ink-100 transition-colors hover:bg-ink-50"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar url={t.avatar_url} nombre={t.nombre} />
                          <div>
                            <div className="font-medium text-ink-900">{t.nombre}</div>
                            {t.cargo && (
                              <div className="text-xs text-ink-500">{t.cargo}</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <StarsDisplay value={t.rating} />
                      </td>
                      <td className="max-w-md px-4 py-3">
                        <span
                          title={t.comentario}
                          className="cursor-help text-sm text-ink-700"
                        >
                          {truncate(t.comentario, 100)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => toggleActive.mutate(t)}
                          disabled={toggleActive.isPending}
                          className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-50 ${
                            t.is_active
                              ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
                          }`}
                        >
                          {t.is_active ? (
                            <>
                              <Eye className="size-3" /> Activo
                            </>
                          ) : (
                            <>
                              <EyeOff className="size-3" /> Inactivo
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          value={sortVal}
                          onChange={(e) =>
                            setSortDraft((p) => ({
                              ...p,
                              [String(t.id)]: Number(e.target.value),
                            }))
                          }
                          onBlur={() => onCommitSort(t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              ;(e.target as HTMLInputElement).blur()
                            }
                          }}
                          className="w-20 rounded-md border border-ink-200 bg-white px-2 py-1 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => openEdit(t)}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                            title="Editar"
                          >
                            <Edit className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => onDelete(t)}
                            disabled={del.isPending}
                            className="rounded-md p-1.5 text-ink-500 hover:bg-red-100 hover:text-red-700 disabled:opacity-50"
                            title="Eliminar"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear/editar */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal()
          }}
        >
          <div className="relative max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-ink-200 bg-white px-6 py-4">
              <h2 className="text-lg font-bold text-ink-900">
                {editingId == null ? 'Nuevo testimonio' : 'Editar testimonio'}
              </h2>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
              >
                <X className="size-5" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-5 p-6">
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {formError}
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    maxLength={120}
                    value={form.nombre}
                    onChange={(e) => setForm((p) => ({ ...p, nombre: e.target.value }))}
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                    Cargo / ciudad
                  </label>
                  <input
                    type="text"
                    maxLength={120}
                    value={form.cargo}
                    onChange={(e) => setForm((p) => ({ ...p, cargo: e.target.value }))}
                    placeholder="Cliente frecuente, CDMX"
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                  />
                </div>
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Comentario <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={4}
                  maxLength={800}
                  value={form.comentario}
                  onChange={(e) => setForm((p) => ({ ...p, comentario: e.target.value }))}
                  className="w-full resize-y rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                />
                <div className="mt-1 text-right text-xs text-ink-400">
                  {form.comentario.length}/800
                </div>
              </div>

              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Rating <span className="text-red-500">*</span>
                </label>
                <StarsInput
                  value={form.rating}
                  onChange={(v) => setForm((p) => ({ ...p, rating: v }))}
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                  Avatar (URL)
                </label>
                <div className="flex items-center gap-3">
                  <Avatar url={form.avatar_url || null} nombre={form.nombre || '?'} />
                  <input
                    type="url"
                    value={form.avatar_url}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, avatar_url: e.target.value }))
                    }
                    placeholder="https://…/foto.jpg"
                    className="flex-1 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                  />
                </div>
                <p className="mt-1 text-xs text-ink-400">
                  Opcional. Si no se proporciona se muestran las iniciales del nombre.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink-600">
                    Orden (sort)
                  </label>
                  <input
                    type="number"
                    value={form.sort_order}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, sort_order: Number(e.target.value) }))
                    }
                    className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-right text-sm tabular-nums focus:border-brand-500 focus:outline-none focus:ring-4 focus:ring-brand-100"
                  />
                  <p className="mt-1 text-xs text-ink-400">
                    Menor número aparece primero.
                  </p>
                </div>
                <div className="flex items-end">
                  <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm((p) => ({ ...p, is_active: e.target.checked }))
                      }
                      className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="font-medium text-ink-700">
                      Visible en el home
                    </span>
                  </label>
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 border-t border-ink-200 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="size-4 animate-spin" />
                  ) : (
                    <Save className="size-4" />
                  )}
                  {editingId == null ? 'Crear' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
